import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Upload, FileText, Download, CheckCircle2, AlertCircle, RefreshCw, Database } from "lucide-react";

const TYPES = [
  { key: "members", label: "Members", desc: "Import students in bulk (name, email, phone, company, address, notes). Random secure passwords are generated — students don't log in." },
  { key: "certificates", label: "Certificates", desc: "Import certs by member email + qualification code. Issue date, expiry date and certificate number required." },
  { key: "courses", label: "Courses", desc: "Import scheduled courses by qualification code. Dates, times, capacity, instructor and pricing supported." },
];

export default function BulkImportSection() {
  const [activeType, setActiveType] = useState("members");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const current = TYPES.find(t => t.key === activeType);

  const reset = () => { setFile(null); setPreview(null); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/import/${activeType}/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeType}-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not download template");
    }
  };

  const runImport = async (dryRun) => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    const setter = dryRun ? setLoading : setCommitting;
    setter(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/${activeType}?dry_run=${dryRun}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      if (dryRun) {
        toast.success(`Preview ready — ${data.summary.create} will be created, ${data.summary.skip} skipped, ${data.summary.error} errors`);
      } else {
        toast.success(`Imported ${data.summary.create} ${activeType} (${data.summary.skip} skipped, ${data.summary.error} errors)`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setter(false);
    }
  };

  const summaryBadge = (k, count, tone) => (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded font-medium ${tone}`}>
      {k}: <strong>{count}</strong>
    </span>
  );

  return (
    <section className="bg-white rounded-lg border border-border" data-testid="bulk-import-section">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Database size={18} className="text-slate-600" />
          <h2 className="font-heading font-semibold">Bulk CSV Import</h2>
          <Badge variant="outline" className="text-[10px]">admin only</Badge>
        </div>
      </div>
      <div className="p-5 space-y-4">
        {/* Type tabs */}
        <div className="flex flex-wrap gap-2">
          {TYPES.map(t => (
            <button
              key={t.key}
              onClick={() => { setActiveType(t.key); reset(); }}
              className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${activeType === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-border hover:bg-slate-50"}`}
              data-testid={`import-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{current?.desc}</p>

        <div className="flex flex-wrap items-center gap-3">
          <Label className="flex items-center gap-2 cursor-pointer">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              className="hidden"
              data-testid={`import-file-${activeType}`}
            />
            <span className="inline-flex items-center gap-1.5 text-sm border border-border rounded px-3 py-1.5 hover:bg-slate-50">
              <Upload size={14} /> Choose CSV
            </span>
            {file && <span className="text-xs text-muted-foreground flex items-center gap-1"><FileText size={12} />{file.name}</span>}
          </Label>

          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid={`import-template-${activeType}`}>
            <Download size={14} className="mr-1.5" /> Download template
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!file || loading}
            onClick={() => runImport(true)}
            data-testid={`import-preview-${activeType}`}
          >
            {loading ? <RefreshCw size={14} className="mr-1.5 animate-spin" /> : null}
            Preview
          </Button>
          <Button
            size="sm"
            disabled={!preview || !preview.summary?.create || committing}
            onClick={() => runImport(false)}
            data-testid={`import-commit-${activeType}`}
          >
            {committing ? <RefreshCw size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
            Import {preview?.summary?.create ? `(${preview.summary.create})` : ""}
          </Button>
        </div>

        {preview && (
          <div className="mt-2 space-y-2" data-testid={`import-result-${activeType}`}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-slate-700">Result:</span>
              {summaryBadge("total", preview.summary.total, "bg-slate-100 text-slate-700")}
              {summaryBadge(preview.dry_run ? "to create" : "created", preview.summary.create, "bg-emerald-100 text-emerald-800")}
              {summaryBadge("skipped", preview.summary.skip, "bg-amber-100 text-amber-800")}
              {summaryBadge("errors", preview.summary.error, "bg-red-100 text-red-800")}
              {preview.dry_run && preview.summary.create > 0 && (
                <span className="text-[11px] text-emerald-700 ml-1 inline-flex items-center gap-1">
                  <CheckCircle2 size={12} /> Dry-run preview — click Import to commit
                </span>
              )}
            </div>
            <div className="border border-border rounded overflow-hidden max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-muted-foreground sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium w-12">Line</th>
                    <th className="text-left px-3 py-1.5 font-medium w-20">Status</th>
                    <th className="text-left px-3 py-1.5 font-medium">Identifier</th>
                    <th className="text-left px-3 py-1.5 font-medium">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map(r => (
                    <tr key={r.line} className="border-t border-border" data-testid={`import-row-${r.line}`}>
                      <td className="px-3 py-1.5 text-muted-foreground">{r.line}</td>
                      <td className="px-3 py-1.5">
                        {r.status === "create" || r.status === "created" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> {r.status}</span>
                        ) : r.status === "skip" ? (
                          <span className="text-amber-700">skip</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700"><AlertCircle size={12} /> error</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700">
                        {r.email || r.member_email || r.title || r.qualification_code || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground">
                        {(r.errors || []).join("; ")}
                        {r.warnings?.length > 0 && (
                          <span className="text-amber-700">{r.errors?.length ? " · " : ""}{r.warnings.join("; ")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
